import { LoadingOutlined } from '@ant-design/icons';
import { Button, DatePicker, Empty, Select, Spin, Typography } from 'antd';
import moment, { Moment } from 'moment';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../../../api/Api';
import { useIsFwe } from '../../../../../hooks/useIsFwe';
import { usePointStore } from '../../../../../store/pointStore';
import { IBestDataset, IItemsValidation } from '../../../../../types/analytics';
import { IGetBasisChartV2ApiPayload } from '../../../../../types/chart';
import { MarkerItem } from '../../../../../types/home';
import { IEnrichedMetadata } from '../../../../../types/metadata';
import { UserGroupPermissions } from '../../../../../types/userGroupPermissions';
import { ValidationTabsEnum } from '../../../../../types/validation';
import {
  DownloadCheckboxKey,
  GLOBAL_HD_DTU_ID,
  TS_PLOT_VALIDATION,
} from '../../../../../utils/common';
import { DT0Format } from '../../../../../utils/StaticVariable';
import MultipleLineSeries from '../../../../common/Chart/MultipleLineChart';
import styles from '../../../../features/ResultPanel/Style/ValidationPanel.module.scss';
import { AnalyticsSection } from '../../../Analytics/components/Layout/AnalyticsSection';
import { ChartBox } from '../../../ResultPanel/component/ChartBox';

const { Text } = Typography;

interface ITimeSeries {
  variable: string | number;
  setVariable: (item: string | number) => void;
  startDate: Moment | undefined;
  setStartDate: (item: Moment) => void;
  endDate: Moment | undefined;
  setEndDate: (item: Moment) => void;
  validInSituPoint?: boolean;
  activePoint?: MarkerItem;
  activeTab: number;
  enrichedMetadata?: IEnrichedMetadata[];
  listParameters: IItemsValidation[];
  dateRange?: [moment.Moment, moment.Moment];
  tooltipName?: {
    insitu?: string;
    point?: string;
  };
  activeCategoryKey: string;
  setActiveCategory: (value: any) => void;
  activePanel: string;
  userGroupPermissions: UserGroupPermissions;
  datasetId: string;
  activeInsituType: string;
  bestDataset?: IBestDataset[];
  timeStepInterval: string;
  datasetChange: (item: string) => void;
  notValidInsitu: ReactNode;
}

const TimeSeries: React.FC<ITimeSeries> = ({
  activeCategoryKey,
  validInSituPoint,
  activePoint,
  enrichedMetadata,
  tooltipName,
  listParameters,
  dateRange,
  activeTab,
  activePanel,
  datasetId,
  bestDataset,
  activeInsituType,
  timeStepInterval,
  notValidInsitu,
  variable,
  setVariable,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  datasetChange,
}) => {
  const [yAxisLabel, setYAxisLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [seriesDataPoint, setSeriesDataPoint] = useState<
    Array<{ Time: Date; Value: number }>
  >([]);
  const [seriesDataInsitu, setSeriesDataInsitu] = useState<
    Array<{ Time: Date; Value: number }>
  >([]);
  const [error, setError] = useState('');
  const isFwe = useIsFwe(activePoint?.dataset);
  const fwePoints = usePointStore((state) => state.fwePoints);

  useEffect(() => {
    setSeriesDataInsitu([]);
    setSeriesDataPoint([]);
    setError('');
    setVariable('');
  }, [activePoint?.coordinates, datasetId]);

  useEffect(() => {
    const shouldLoadTimeSeries = () => {
      return (
        enrichedMetadata &&
        listParameters.length > 0 &&
        validInSituPoint &&
        dateRange &&
        activePanel === ValidationTabsEnum.INSITU_TAB &&
        error === '' &&
        !loading &&
        activeTab === TS_PLOT_VALIDATION &&
        seriesDataPoint.length === 0 &&
        seriesDataInsitu.length === 0
      );
    };

    if (shouldLoadTimeSeries()) {
      if (!startDate && !endDate) {
        setStartDate(moment(dateRange?.[0]));
        setEndDate(moment(dateRange?.[1]));
      }
      let parameter = listParameters[0];
      if (!isEmpty(variable)) {
        const findParam = listParameters.find((item) => item.name === variable);
        parameter = findParam!;
      }
      setVariable(parameter.name);
      setYAxisLabel(`${parameter.details} [${parameter.unit}]`);

      const values: IGetBasisChartV2ApiPayload = {
        datasetName: datasetId,
        parameters: listParameters[0].details,
        startDate: moment(dateRange?.[0]).format(DT0Format),
        endDate: moment(dateRange?.[1]).format(DT0Format),
      };

      onLoadTimeSeries(
        values,
        enrichedMetadata!,
        parameter.name,
        activeCategoryKey
      ).catch(console.error);
    }
  }, [
    activePanel,
    error,
    loading,
    activeTab,
    enrichedMetadata,
    validInSituPoint,
    listParameters,
    dateRange,
    datasetId,
    activeCategoryKey,
  ]);

  const onLoadTimeSeries = async (
    values: IGetBasisChartV2ApiPayload,
    enrichedMetadata: IEnrichedMetadata[],
    variable: string | number,
    type: string
  ) => {
    if (activeCategoryKey !== 'SW') {
      return;
    }
    const category = bestDataset?.find(
      (bd: IBestDataset) => bd.id === values.datasetName
    );
    if (category !== undefined) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === values.datasetName &&
          task.CoordinatesString === activePoint?.coordinates.join()
      );
      if (task) {
        try {
          setLoading(true);
          let pointName = activePoint?.label ?? '';
          if (isFwe) {
            if (!activePoint?.fwePointName) {
              const findFwePointName = fwePoints.find(
                (f) => f.datasetId === datasetId
              )?.name;
              pointName = findFwePointName ?? activePoint?.label ?? '';
            } else {
              pointName = activePoint.fwePointName;
            }
          }
          const pointChart = await Api.chart.getBasicChartV2(
            task.OrderId,
            pointName,
            values.datasetName,
            values.startDate,
            values.endDate,
            activePoint?.coordinates.join() ?? '',
            datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
            values.parameters
          );
          if (pointChart?.Data?.Items) {
            setSeriesDataPoint(
              pointChart.Data.Items.map(
                (ts: { Time: string; [key: string]: string }) => {
                  return {
                    Time: moment(ts.Time, 'YYYY-MM-DD HH:mm:ss').toDate(),
                    Value: parseFloat(
                      ts[values.parameters?.split(' ').join('_') || '']
                    ),
                  };
                }
              )
            );
          }

          if (activeInsituType === 'mood') {
            const insituUploadChart = await Api.chart.getBasicChartV2(
              crypto.randomUUID(),
              tooltipName?.insitu ?? '',
              `insitu-${tooltipName?.insitu}`,
              values.startDate,
              values.endDate,
              activePoint?.coordinates.join() ?? '',
              datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
              values.parameters
            );
            if (insituUploadChart?.Data?.Items) {
              setSeriesDataInsitu(
                insituUploadChart.Data.Items.map(
                  (ts: { Time: string; [key: string]: string }) => {
                    return {
                      Time: moment(ts.Time, 'YYYY-MM-DD HH:mm:ss').toDate(),
                      Value: parseFloat(
                        ts[values.parameters?.split(' ').join('_') || '']
                      ),
                    };
                  }
                )
              );
            }
          } else {
            const insituChart = await Api.chart.getTimeseriesInsitu(
              tooltipName?.insitu ?? '',
              task.OrderId,
              variable,
              type,
              values.startDate,
              values.endDate
            );

            if (insituChart?.length > 0) {
              setSeriesDataInsitu(
                insituChart.map((ts: { dateTime: string; value: string }) => {
                  return {
                    Time: moment(ts.dateTime, 'YYYY-MM-DD HH:mm:ss').toDate(),
                    Value:
                      parseFloat(ts.value) === -9999
                        ? undefined
                        : parseFloat(ts.value),
                  };
                })
              );
            }
          }
        } catch {
          setError('Something went wrong');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const onChangeStartDate = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const onChangeEndDate = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onSelectChange = (value: string | number) => {
    setVariable(value);
  };

  const handleUpdate = () => {
    if (enrichedMetadata && datasetId && startDate && endDate) {
      const findParameters = listParameters.find((lp) => lp.name === variable);
      const params = findParameters?.details ?? '';
      const unit = findParameters?.unit ?? '';
      setYAxisLabel(`${params} [${unit}]`);
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: datasetId,
        parameters: params,
        startDate: startDate?.format(DT0Format),
        endDate: endDate?.format(DT0Format),
      };
      onLoadTimeSeries(
        values,
        enrichedMetadata,
        variable,
        activeCategoryKey
      ).catch(console.error);
    }
  };

  function disabledStartDate(current: Moment): boolean {
    const minDate = startDate;
    const maxDate = endDate;

    if (!current || !maxDate || !minDate) {
      return false;
    }

    const isBeforeMinDate = current.isBefore(minDate, 'day');
    const isAfterMaxDate = current.isAfter(maxDate, 'day');

    return isBeforeMinDate || isAfterMaxDate;
  }

  function disabledEndDate(current: Moment): boolean {
    const maxDate = endDate;

    if (!startDate) {
      return true;
    }

    const isBeforeStartDate = current.isBefore(startDate, 'day');
    const isAfterMaxDate = current.isAfter(maxDate, 'day');

    return isBeforeStartDate || isAfterMaxDate;
  }

  const datasetLabel = useMemo(() => {
    const group = activePoint?.downloadPage.find(
      (g) => g.radioKey === activeCategoryKey
    );
    const option = group?.options.find((opt) => opt.id === group.datasetId);

    return option?.value;
  }, [activePoint, datasetId]);

  return (
    <AnalyticsSection
      plotInputContent={
        <div className={styles.insituForm}>
          <div>
            <Text>Dataset</Text>
            <Select
              id='Validation-Timeseries_Dataset_Change'
              value={datasetId}
              style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
              onChange={datasetChange}
              placeholder='Select dataset'
              disabled={
                !validInSituPoint ||
                listParameters.length === 0 ||
                loading ||
                activeCategoryKey !== DownloadCheckboxKey.SW
              }
            >
              {bestDataset?.map((item: IBestDataset) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.value}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Text>Variable</Text>
            <Select
              id='Validation-Timeseries_Variable_Change'
              style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
              placeholder='Select variable'
              onChange={onSelectChange}
              options={listParameters.map((item) => ({
                value: item.name,
                label: `${item.details}${item.unit ? ` (${item.unit})` : ''}`,
              }))}
              value={variable}
              disabled={
                !validInSituPoint ||
                listParameters.length === 0 ||
                loading ||
                activeCategoryKey !== DownloadCheckboxKey.SW
              }
            />
          </div>

          <div>
            <Text>Start Date</Text>
            <DatePicker
              id='Validation-Timeseries_Start_Date_Change'
              onChange={onChangeStartDate}
              style={{ width: '100%', marginTop: '4px' }}
              placeholder='Select start date'
              disabledDate={disabledStartDate}
              disabled={
                loading ||
                !validInSituPoint ||
                activeCategoryKey !== DownloadCheckboxKey.SW
              }
              value={startDate ? moment(startDate) : null}
            />
          </div>

          <div>
            <Text>End Date</Text>
            <DatePicker
              id='Validation-Timeseries_End_Date_Change'
              onChange={onChangeEndDate}
              disabled={
                loading ||
                !validInSituPoint ||
                activeCategoryKey !== DownloadCheckboxKey.SW
              }
              disabledDate={disabledEndDate}
              style={{ width: '100%', marginTop: '4px' }}
              placeholder='Select end date'
              value={endDate ? moment(endDate) : null}
            />
          </div>
        </div>
      }
      updateContent={
        <Button
          id='Validation-TimeSeries_Update_Button'
          type='primary'
          disabled={
            !validInSituPoint ||
            listParameters.length === 0 ||
            loading ||
            activeCategoryKey !== DownloadCheckboxKey.SW
          }
          onClick={handleUpdate}
          style={{ width: 100 }}
        >
          Update
        </Button>
      }
      isLoading={loading}
      chartContent={
        validInSituPoint ? (
          <ChartBox
            key={'Time Series Plot'}
            title={`${activePoint?.label}(${activePoint?.coordinates[0]},${activePoint?.coordinates[1]})`}
            height={loading || listParameters.length === 0 ? '200px' : 500}
            plot={
              activeCategoryKey !== DownloadCheckboxKey.SW ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                  id='Validation-In-situ-not-available-info'
                >
                  In-situ validation currently only support for waves dataset
                </div>
              ) : loading || listParameters.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 50 }} spin />
                    }
                  />
                </div>
              ) : error ? (
                <div>{error}</div>
              ) : seriesDataInsitu.length !== 0 ||
                seriesDataPoint.length !== 0 ? (
                <MultipleLineSeries
                  variable={yAxisLabel}
                  data={[seriesDataInsitu, seriesDataPoint]}
                  names={[`Measurement (in situ)`, datasetLabel ?? 'Point']}
                  colors={['green', 'blue']}
                  title={`${activePoint?.label}(${activePoint?.coordinates[0]}°E,${activePoint?.coordinates[1]}°N)`}
                />
              ) : (
                <Empty />
              )
            }
            dataset=''
          />
        ) : (
          notValidInsitu
        )
      }
    />
  );
};

export default TimeSeries;
